/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2025, Zumper
 * @description   useBodyClientSize
 */

import { debounce } from '@zcode/utils'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import invariant from 'tiny-invariant'
import { v4 as uuidV4 } from 'uuid'

interface BodyClientSizeContextValue {
  registerListener: (uuid: string) => void
  size: readonly [width: number, height: number]
  unregisterListener: (uuid: string) => void
}

const BodyClientSizeContext = createContext<BodyClientSizeContextValue | null>(null)

export const BodyClientSizeProvider = ({ children }: React.PropsWithChildren) => {
  const [size, setSize] = useState<readonly [width: number, height: number]>([0, 0])
  const listenersRef = useRef(new Set<string>())
  const updateSize = useCallback(
    debounce(
      () => {
        if (typeof window !== 'undefined') {
          setSize([window.innerWidth, window.innerHeight])
        }
      },
      50,
      500
    ),
    []
  )
  const registerListener = useCallback((uuid: string) => {
    if (!listenersRef.current.size) {
      window.addEventListener('resize', updateSize)
      updateSize()
    }
    listenersRef.current.add(uuid)
  }, [])
  const unregisterListener = useCallback((uuid: string) => {
    listenersRef.current.delete(uuid)
    if (!listenersRef.current.size) {
      window.removeEventListener('resize', updateSize)
    }
  }, [])
  useEffect(() => {
    return () => window.removeEventListener('resize', updateSize)
  }, [])
  const currentContextValue = useMemo(() => {
    return {
      registerListener,
      size,
      unregisterListener,
    }
  }, [size])
  return (
    <BodyClientSizeContext.Provider value={currentContextValue}>
      {children}
    </BodyClientSizeContext.Provider>
  )
}

export const useBodyClientSize = () => {
  const context = useContext(BodyClientSizeContext)
  invariant(context, 'useBodyClientSize must appear as descendent of BodyClientSizeProvider')
  const { registerListener, size, unregisterListener } = context
  useEffect(() => {
    const id = uuidV4()
    registerListener(id)
    return () => {
      unregisterListener(id)
    }
  }, [])
  return size
}
